import { useState } from 'react';

import axios from 'axios';
import { APIEndpoint } from 'common/enum'; // Adjust import path accordingly
import {
  allowedFileTypes,
  largeFileTypes,
  logoFileName,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_MB_LARGE_FILES,
} from 'config/config';
import { popupNotification } from 'notifications/ToastNotification'; // Assuming this is the correct notification method
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { fileNameValidation } from 'validation/validationScheme';

const defaultErrorMessage = 'File upload failed. Please try again.';

interface optionsInterface {
  isQR?: boolean;
  fileName?: string;
  fileTypesAllowed?: string[];
}

export const useFileUpload = (options?: optionsInterface) => {
  const [progress, setProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [returnFiles, setReturnFiles] = useState<File[]>([]);
  const { accessToken } = useSelector((state: RootState) => state.auth);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_FILE_SIZE_BYTES_FOR_LARGE_FILES =
    MAX_FILE_SIZE_MB_LARGE_FILES * 1024 * 1024;
  const [uploadedUrl, setUploadedUrl] = useState([] as string[]);
  const uploadFiles = async (
    uuid: string | undefined | null,
    folderId: string | null,
    firstName: string | null,
    lastName: string | null,
    newFolderPath: string,
    setModalOpen: (isOpen: boolean) => void,
    home: string | null = 'Home'
  ) => {
    setIsUploading(true);

    const uploadPromises = files.map(async (file) => {
      // Check if file size exceeds the limit
      const msxFileSize = largeFileTypes.includes(file.type)
        ? MAX_FILE_SIZE_BYTES_FOR_LARGE_FILES
        : MAX_FILE_SIZE_BYTES;
      if (file.size > msxFileSize) {
        setIsSuccess(false);
        popupNotification(
          `File ${file.name} is too large. Maximum allowed size is ${
            largeFileTypes.includes(file.type)
              ? MAX_FILE_SIZE_BYTES_FOR_LARGE_FILES
              : MAX_FILE_SIZE_BYTES
          }MB.`,
          false
        );
        return; // Skip uploading this file
      }
      if (!fileNameValidation.test(file.name)) {
        setIsSuccess(false);
        popupNotification(`Invalid file name ${file.name}`, false);
        return;
      }
      if (options?.fileTypesAllowed && options.fileTypesAllowed.length) {
        if (!options?.fileTypesAllowed.includes(file.type)) {
          setIsSuccess(false);
          popupNotification(
            `File ${file.name} is not a valid file type.`,
            false
          );
          return;
        }
      }
      if (
        !allowedFileTypes.includes(file.type) &&
        !largeFileTypes.includes(file.type)
      ) {
        setIsSuccess(false);
        popupNotification(`File ${file.name} is not a valid file type.`, false);
        return; // Skip uploading this file
      }

      try {
        const { data } = await axios.post(
          APIEndpoint.uploadFile,
          {
            fileName: `${uuid}/${folderId ? folderId + '/' : ''}${
              home ? home + '/' : ''
            }${newFolderPath.length > 0 ? newFolderPath + '/' : ''}${
              home
                ? file.name
                : options?.fileName
                  ? options?.fileName + '.' + file.name.split('.').pop()
                  : logoFileName
            }`,
            uploadedBy: `${firstName} ${lastName}`,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + accessToken,
            },
          }
        );

        // Directly use the upload URL from the response
        const uploadUrl = data.upload_url.url;
        setUploadedUrl((prev) => [...prev, uploadUrl]);
        const formData = new FormData();
        if (data.upload_url.fields) {
          Object.entries(data.upload_url.fields).forEach(([k, v]: any) => {
            formData.append(k, v);
          });
        } else {
          console.error('No fields found in the response');
        }

        formData.append('file', file);

        await axios.post(uploadUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total ?? progressEvent.loaded;
            const progressPercentage = Math.round(
              (progressEvent.loaded / total) * 100
            );
            setProgress((prevProgress) => ({
              ...prevProgress,
              [file.name]: progressPercentage,
            }));
          },
        });
        setIsSuccess(true);
        popupNotification('Files uploaded successfully', true);
      } catch (error) {
        setIsSuccess(false);
        popupNotification(defaultErrorMessage, false);
      }
    });

    await Promise.all(uploadPromises).then(() => {
      // popupNotification('Files uploaded successfully', true);
      setModalOpen(false);
      setFiles([]); // Clear files after upload
      setReturnFiles([]); // Clear return files after upload
      setProgress({}); // Reset progress after upload
      setUploadedUrl([]); // Clear uploaded URLs after upload
      setIsUploading(false);
    });
  };

  const setSelectedFiles = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
    setReturnFiles(selectedFiles);
    setProgress({}); // Reset progress when new files are selected
  };

  const clearFiles = () => {
    setFiles([]);
    setReturnFiles([]);
    setProgress({});
    setUploadedUrl([]);
  };

  return {
    uploadFiles,
    progress,
    isUploading,
    setSelectedFiles,
    clearFiles,
    files: returnFiles,
    uploadedUrl,
    isSuccess,
  };
};
